/**
 * Utility functions for route replay: speed segmentation, position interpolation, stop detection.
 * All speed values from Traccar are in knots — converted to km/h before comparison with thresholds.
 */

import { lerpPosition } from '@/lib/lerpPosition';
import { haversine } from './replayTrack';

/** Default speed thresholds in km/h */
export const DEFAULT_SPEED_THRESHOLDS = { slow: 60, moderate: 100 };

/** Meters a fix must move from the last KEPT point before it counts as real travel, not GPS jitter. */
export const JITTER_RADIUS_M = 20;

/**
 * Collapse a stationary GPS-jitter cloud into its start/end points so the drawn
 * route doesn't zigzag while a device sits parked. Keeps the first and last fix
 * unconditionally; drops any point in between that's within `radiusM` of the last
 * KEPT point (not the raw previous point), so a slow drift doesn't sneak past one
 * short hop at a time.
 *
 * @param {Array} positions - Array of Traccar position objects, ascending by time
 * @param {number} radiusM - Jitter radius in meters
 * @returns {Array} Filtered position array
 */
export function dedupeJitter(positions, radiusM = JITTER_RADIUS_M) {
  if (!positions || positions.length <= 2) return positions || [];
  const out = [positions[0]];
  for (let i = 1; i < positions.length - 1; i++) {
    if (haversine(out[out.length - 1], positions[i]) >= radiusM) out.push(positions[i]);
  }
  out.push(positions[positions.length - 1]);
  return out;
}

/**
 * Get color for a given speed in km/h based on thresholds.
 * @param {number} speedKmh - Speed in km/h
 * @param {{ slow: number, moderate: number }} thresholds
 * @returns {string} Hex color
 */
export function getSpeedColor(speedKmh, thresholds = DEFAULT_SPEED_THRESHOLDS) {
  if (speedKmh <= thresholds.slow) return '#10b981';    // green
  if (speedKmh <= thresholds.moderate) return '#f59e0b'; // yellow
  return '#ef4444';                                       // red
}

/**
 * Build speed-colored GeoJSON FeatureCollection from position array.
 * Merges consecutive same-color segments into multi-point LineStrings to reduce feature count.
 *
 * @param {Array} positions - Array of Traccar position objects
 * @param {{ slow: number, moderate: number }} thresholds - Speed thresholds in km/h
 * @returns {{ type: 'FeatureCollection', features: Array }} GeoJSON FeatureCollection
 */
export function buildSpeedSegments(positions, thresholds = DEFAULT_SPEED_THRESHOLDS) {
  if (!positions || positions.length < 2) {
    return { type: 'FeatureCollection', features: [] };
  }

  const features = [];
  let currentColor = null;
  let currentCoords = [];
  let currentSpeed = 0;

  for (let i = 0; i < positions.length; i++) {
    const pos = positions[i];
    const speedKmh = (pos.speed || 0) * 1.852;
    const color = getSpeedColor(speedKmh, thresholds);
    const coord = [pos.longitude, pos.latitude];

    if (color === currentColor) {
      // Extend current segment
      currentCoords.push(coord);
      currentSpeed = speedKmh;
    } else {
      // Finish previous segment (needs at least 2 points)
      if (currentColor && currentCoords.length >= 2) {
        features.push({
          type: 'Feature',
          properties: { speed: currentSpeed, color: currentColor },
          geometry: { type: 'LineString', coordinates: currentCoords },
        });
      }
      // Start new segment — include previous point for continuity
      if (i > 0) {
        const prevPos = positions[i - 1];
        currentCoords = [[prevPos.longitude, prevPos.latitude], coord];
      } else {
        currentCoords = [coord];
      }
      currentColor = color;
      currentSpeed = speedKmh;
    }
  }

  // Flush last segment
  if (currentColor && currentCoords.length >= 2) {
    features.push({
      type: 'Feature',
      properties: { speed: currentSpeed, color: currentColor },
      geometry: { type: 'LineString', coordinates: currentCoords },
    });
  }

  return { type: 'FeatureCollection', features };
}

/**
 * Interpolate position along route at a given normalized progress (0-1).
 *
 * @param {Array} positions - Array of Traccar position objects
 * @param {number} progress - Normalized progress (0.0 to 1.0)
 * @returns {{ latitude: number, longitude: number, speed: number, course: number, fixTime: string } | null}
 */
export function interpolatePosition(positions, progress) {
  if (!positions || positions.length === 0) return null;
  if (positions.length === 1) {
    const p = positions[0];
    return {
      latitude: p.latitude,
      longitude: p.longitude,
      speed: p.speed,
      course: p.course,
      fixTime: p.fixTime,
    };
  }

  const totalPoints = positions.length - 1;
  const exactIndex = Math.max(0, Math.min(1, progress)) * totalPoints;
  const i = Math.floor(exactIndex);
  const t = exactIndex - i;

  if (i >= totalPoints) {
    const p = positions[totalPoints];
    return {
      latitude: p.latitude,
      longitude: p.longitude,
      speed: p.speed,
      course: p.course,
      fixTime: p.fixTime,
    };
  }

  const a = positions[i];
  const b = positions[i + 1];

  // Shared lerp lives in src/lib/lerpPosition.js so live animation + replay
  // use one implementation. Note: lerpPosition takes b.course for the result
  // (live animation wants the "where we're heading" bearing). For historical
  // replay we want the segment's start bearing instead, so we overlay it.
  return { ...lerpPosition(a, b, t), course: a.course };
}

/**
 * Find stops in route where vehicle was stationary for a minimum duration.
 *
 * @param {Array} positions - Array of Traccar position objects
 * @param {number} minStopDurationMs - Minimum stop duration in ms (default 2 minutes)
 * @returns {Array<{ latitude: number, longitude: number, startTime: string, endTime: string, duration: number }>}
 */
export function findStops(positions, minStopDurationMs = 120000) {
  if (!positions || positions.length < 2) return [];

  const stops = [];
  let stopStart = null;
  let stopLat = 0;
  let stopLng = 0;

  for (let i = 0; i < positions.length; i++) {
    const pos = positions[i];
    const speedKmh = (pos.speed || 0) * 1.852;

    if (speedKmh < 1) {
      // Considered stopped (< 1 km/h accounts for GPS drift)
      if (!stopStart) {
        stopStart = pos;
        stopLat = pos.latitude;
        stopLng = pos.longitude;
      }
    } else {
      // Moving — check if previous stop was long enough
      if (stopStart) {
        const duration = new Date(positions[i - 1].fixTime).getTime() - new Date(stopStart.fixTime).getTime();
        if (duration >= minStopDurationMs) {
          stops.push({
            latitude: stopLat,
            longitude: stopLng,
            startTime: stopStart.fixTime,
            endTime: positions[i - 1].fixTime,
            duration,
          });
        }
        stopStart = null;
      }
    }
  }

  // Check trailing stop
  if (stopStart) {
    const lastPos = positions[positions.length - 1];
    const duration = new Date(lastPos.fixTime).getTime() - new Date(stopStart.fixTime).getTime();
    if (duration >= minStopDurationMs) {
      stops.push({
        latitude: stopLat,
        longitude: stopLng,
        startTime: stopStart.fixTime,
        endTime: lastPos.fixTime,
        duration,
      });
    }
  }

  return stops;
}
